/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Studio Main Application Workspace
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef } from 'react';
import { AppHeader } from './components/shell/AppHeader';
import { AppStatusBar } from './components/shell/AppStatusBar';
import { NodeShelf } from './components/shelf/NodeShelf';
import { Inspector } from './components/inspector/Inspector';
import { OutputPreviewPanel } from './components/preview/OutputPreviewPanel';
import { Viewport } from './viewport/domSvg/Viewport';
import { useDomainBridge } from './stores/domainBridge';
import { useViewportStore } from './stores/viewportStore';
import { loadLatestProjectFromDb } from './persistence/indexedDb';
import { defaultNodeRegistry } from './nodes';
import { addNode, addEdge } from './domain/graph';
import { deserializeProject } from './domain/serialization';

export const App: React.FC = () => {
  const { domainState, loadCanonicalGraph, runExecution } = useDomainBridge();
  const { shelfOpen, inspectorOpen } = useViewportStore();
  const initializedRef = useRef(false);

  // Initialize: attempt restore from IndexedDB or populate starter pipeline
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    async function initializeStudio() {
      try {
        const savedProject = await loadLatestProjectFromDb();
        if (savedProject) {
          const validated = deserializeProject(JSON.stringify(savedProject));
          if (
            validated.success &&
            validated.state &&
            Object.keys(validated.state.canonical.nodes).length > 0
          ) {
            loadCanonicalGraph(validated.state.canonical, validated.projectName);
            return;
          }
        }
      } catch (err) {
        console.warn('Could not load or validate project from IndexedDB:', err);
      }

      // Populate default Phase 1 visual demonstration pipeline:
      // Image Source (50, 80) -> GPU Halftone (340, 80) -> Output Preview (650, 80)
      // Color Input A (50, 360) + Color Input B (50, 480) -> Color Mix (340, 420)
      let initial = domainState;

      const imgNode = defaultNodeRegistry.createDefaultInstance('input.image', { x: 60, y: 80 }, 'node_img')!;
      const halftoneNode = defaultNodeRegistry.createDefaultInstance('filter.halftone', { x: 340, y: 80 }, 'node_halftone')!;
      const previewNode = defaultNodeRegistry.createDefaultInstance('output.preview', { x: 640, y: 80 }, 'node_preview')!;

      const colorA = defaultNodeRegistry.createDefaultInstance('input.color', { x: 60, y: 340 }, 'node_col_a')!;
      colorA.params.value = '#0a0a0a';
      const colorB = defaultNodeRegistry.createDefaultInstance('input.color', { x: 60, y: 480 }, 'node_col_b')!;
      colorB.params.value = '#63d9db';
      const mixNode = defaultNodeRegistry.createDefaultInstance('color.mix', { x: 340, y: 410 }, 'node_col_mix')!;

      initial = addNode(initial, imgNode);
      initial = addNode(initial, halftoneNode);
      initial = addNode(initial, previewNode);
      initial = addNode(initial, colorA);
      initial = addNode(initial, colorB);
      initial = addNode(initial, mixNode);

      // Connect Image -> Halftone -> Output Preview
      initial = addEdge(initial, 'node_img', 'image', 'node_halftone', 'image').state;
      initial = addEdge(initial, 'node_halftone', 'output', 'node_preview', 'source').state;

      // Connect Color A & B -> Mix
      initial = addEdge(initial, 'node_col_a', 'color', 'node_col_mix', 'a').state;
      initial = addEdge(initial, 'node_col_b', 'color', 'node_col_mix', 'b').state;

      loadCanonicalGraph(initial.canonical, 'Untitled Alterly Project');
    }

    initializeStudio();
  }, [domainState, loadCanonicalGraph, runExecution]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--surface-0)',
      }}
    >
      <AppHeader />

      <main
        style={{
          position: 'relative',
          flex: 1,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {shelfOpen && <NodeShelf />}

        <Viewport />

        <OutputPreviewPanel />

        {inspectorOpen && <Inspector />}
      </main>

      <AppStatusBar />
    </div>
  );
};
